import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/services/api';
import {
  Search,
  X,
  FileText,
  RefreshCw,
  Boxes,
  ChevronRight,
} from 'lucide-react';
import clsx from 'clsx';

interface SearchModalProps {
  projectId: string;
  onClose: () => void;
  onSelectIssue: (issueId: string) => void;
  onSelectCycle?: (cycleId: string) => void;
  onSelectModule?: (moduleId: string) => void;
}

export function SearchModal({
  projectId,
  onClose,
  onSelectIssue,
  onSelectCycle,
  onSelectModule,
}: SearchModalProps) {
  const api = useApi();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 200);
    return () => clearTimeout(timer);
  }, [query]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close on escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const { data: searchResults, isLoading } = useQuery({
    queryKey: ['search', projectId, debouncedQuery],
    queryFn: () => api.search(debouncedQuery, projectId),
    enabled: debouncedQuery.length >= 1,
  });

  const { data: states } = useQuery({
    queryKey: ['states', projectId],
    queryFn: () => api.getStates(projectId),
    enabled: !!projectId,
  });

  const getStateById = (stateId: string) => states?.find((s: any) => s.bh_stateid === stateId);

  const hasResults =
    searchResults &&
    (searchResults.workItems?.length > 0 ||
      searchResults.cycles?.length > 0 ||
      searchResults.modules?.length > 0);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[60vh] flex flex-col overflow-hidden">
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
            <Search className="w-5 h-5 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search issues, cycles, modules..."
              className="flex-1 text-lg focus:outline-none placeholder-gray-400"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="p-1 hover:bg-gray-100 rounded text-gray-400"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <kbd className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-500">ESC</kbd>
          </div>

          {/* Results */}
          <div className="flex-1 overflow-y-auto">
            {!debouncedQuery ? (
              <div className="p-8 text-center text-gray-500">
                <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Start typing to search</p>
                <p className="text-sm mt-1">Search across issues, cycles, and modules</p>
              </div>
            ) : isLoading ? (
              <div className="p-8 text-center text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-3">Searching...</p>
              </div>
            ) : !hasResults ? (
              <div className="p-8 text-center text-gray-500">
                <p>No results found for "{debouncedQuery}"</p>
                <p className="text-sm mt-1">Try a different search term</p>
              </div>
            ) : (
              <div className="p-2">
                {/* Work Items */}
                {searchResults.workItems?.length > 0 && (
                  <div className="mb-4">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                      <FileText className="w-3 h-3" />
                      Issues ({searchResults.workItems.length})
                    </div>
                    <div className="space-y-1">
                      {searchResults.workItems.map((item: any) => {
                        const state = getStateById(item._bh_state_value);
                        return (
                          <button
                            key={item.bh_workitemid}
                            onClick={() => {
                              onSelectIssue(item.bh_workitemid);
                              onClose();
                            }}
                            className="w-full px-3 py-2 rounded-lg hover:bg-gray-100 flex items-center gap-3 text-left"
                          >
                            <span
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: state?.bh_color || '#6b7280' }}
                            />
                            <span className="text-sm text-gray-500 font-mono">
                              #{item.bh_sequenceid}
                            </span>
                            <span className="flex-1 text-sm text-gray-900 truncate">
                              {item.bh_name}
                            </span>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Cycles */}
                {searchResults.cycles?.length > 0 && (
                  <div className="mb-4">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                      <RefreshCw className="w-3 h-3" />
                      Cycles ({searchResults.cycles.length})
                    </div>
                    <div className="space-y-1">
                      {searchResults.cycles.map((cycle: any) => (
                        <button
                          key={cycle.bh_cycleid}
                          onClick={() => {
                            onSelectCycle?.(cycle.bh_cycleid);
                            onClose();
                          }}
                          className="w-full px-3 py-2 rounded-lg hover:bg-gray-100 flex items-center gap-3 text-left"
                        >
                          <RefreshCw className="w-4 h-4 text-blue-500" />
                          <span className="flex-1 text-sm text-gray-900 truncate">
                            {cycle.bh_name}
                          </span>
                          <span
                            className={clsx(
                              'px-2 py-0.5 text-xs rounded-full',
                              cycle.bh_status === 'current'
                                ? 'bg-blue-100 text-blue-700'
                                : cycle.bh_status === 'completed'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-700'
                            )}
                          >
                            {cycle.bh_status}
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Modules */}
                {searchResults.modules?.length > 0 && (
                  <div className="mb-4">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                      <Boxes className="w-3 h-3" />
                      Modules ({searchResults.modules.length})
                    </div>
                    <div className="space-y-1">
                      {searchResults.modules.map((module: any) => (
                        <button
                          key={module.bh_moduleid}
                          onClick={() => {
                            onSelectModule?.(module.bh_moduleid);
                            onClose();
                          }}
                          className="w-full px-3 py-2 rounded-lg hover:bg-gray-100 flex items-center gap-3 text-left"
                        >
                          <Boxes className="w-4 h-4 text-purple-500" />
                          <span className="flex-1 text-sm text-gray-900 truncate">
                            {module.bh_name}
                          </span>
                          <span
                            className={clsx(
                              'px-2 py-0.5 text-xs rounded-full',
                              module.bh_status === 'in_progress'
                                ? 'bg-blue-100 text-blue-700'
                                : module.bh_status === 'completed'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-700'
                            )}
                          >
                            {module.bh_status}
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">↑</kbd>
              <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">↓</kbd>
              to navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">Enter</kbd>
              to select
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
